import React, { useEffect, useRef, useState } from 'react';
import { Card, Input, InputRef, message, Modal, Spin, Table, Tooltip, Tag, Button } from 'antd';
import { CopyOutlined, EditOutlined, EyeTwoTone, InfoCircleOutlined, LockTwoTone } from '@ant-design/icons';

import '../../styles/VersionManagement.scss';
import { useNavigate, Link } from 'react-router-dom';
import { title } from 'process';
import { cloneDealVersion, createNewPricingDealVersion, getPricingDealVersions, UnlockPricingDeal } from '../../services/Apis';
import dayjs from 'dayjs';
import LogoutPopUp from '../../components/LogoutPopUp';
import { currencySymbolsMap } from '../../context/CurrencyContext';

const { TextArea } = Input;
interface DealEconomicsSummariesData {
  dealDetails: any;
  expanded: boolean
}

const DealEconomicsSummaries = [
  {
    'Blended Hourly Price': '$ 120',
    'Total Deal Price': '$ 482,763',
    'Deal Discount Amount': '$ 10,888',
    'Net Deal Price': '$ 431,875',
    'Total Deal Labor Cost': '$ 201,810',
    'Projected Client Margin %': '53.3%',
    'Target Client Margin': '56.0%',
    'Variance to Target Margin': '-2.7%',
    'Updated By': 'Pavan Attli',
    'Updated On': 'Sept 15, 2024, 12:16:00 PM EST',
    'ODE Status': 'Draft'
  },
  {
    'Blended Hourly Price': '$ 120',
    'Total Deal Price': '$ 242,763',
    'Deal Discount Amount': '$ 10,888',
    'Net Deal Price': '$ 431,875',
    'Total Deal Labor Cost': '$ 201,810',
    'Projected Client Margin %': '53.3%',
    'Target Client Margin': '56.0%',
    'Variance to Target Margin': '-0.7%',
    'Updated By': 'Pavan Attli',
    'Updated On': 'Sept 15, 2024, 1:06:00 PM EST',
    'ODE Status': 'Submitted'
  },
  {
    'Blended Hourly Price': '$ 120',
    'Total Deal Price': '$ 442,763',
    'Deal Discount Amount': '$ 10,888',
    'Net Deal Price': '$ 431,875',
    'Total Deal Labor Cost': '$ 201,810',
    'Projected Client Margin %': '53.3%',
    'Target Client Margin': '56.0%',
    'Variance to Target Margin': '2.7%',
    'Updated By': 'Pavan Attli',
    'Updated On': 'Sept 15, 2024, 12:36:00 PM EST',
    'ODE Status': 'Draft'
  },
];

const headerList = [
  'MVL TEAM STRUCTURE_SEPT092024',
  'IND TEAM STRUCTURE_SEPT082024',
  'US TEAM STRUCTURE_SEPT072024'
];

// Helper function to format numbers as currency
const formatCurrency = (amount: number, currency: string): string => {
  try {
    return `${currencySymbolsMap[currency] || '$'} ${Math.round(amount).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
  } catch (error) {
    console.error(error);
    return '$ 0';
  }
};

function formatPercentage(value: number) {
  if (value % 1 === 0) {
    return `${value.toString()}%`;
  } else {
    return `${value.toFixed(1)}%`;
  }
}


const DealEconomicsSummariesTable: React.FC<DealEconomicsSummariesData> = ({ dealDetails, expanded }) => {
  const navigate = useNavigate(); // React Router hook for navigation
  const [dealVersionSource, setDealVersionSource] = useState([])
  const [loading, setLoading] = useState(false)
  const [tokenExpired, setTokenExpired] = useState<boolean>(false);
  const [openDuplicateVersionModal, setOpenDuplicateVersionModal] = useState<boolean>(false)
  const [openCreateVersionModal, setOpenCreateVersionModal] = useState<boolean>(false)
  const [openUnlockODEModal, setOpenUnlockODEModal] = useState<boolean>(false);
  const [modalFormInput, setModalFormInput] = React.useState<string>(dealDetails['unlockRequestReason'] ?? '')
  const [additionalComments, setAdditionalComments] = React.useState<string>(dealDetails['unlockComments'] ?? '')
  const [modalFormError, setModalFormError] = React.useState<boolean>(false)

  const [duplicateDeal, setDuplicateDeal] = useState<any>({})
  const [newDuplicateVersionName, setNewDuplicateVersionName] = useState<string>('')
  const [newCreateVersionName, setNewCreateVersionName] = useState<string>('')

  const versionNameInputRef = useRef<InputRef | null>(null);
  const [cloning, setCloning] = useState<boolean>(false)


  useEffect(() => {
    if (!expanded) {
      return
    }
    if (openUnlockODEModal) {
      return
    }
    setLoading(true)
    const fetchVersionDetails = async () => {
      const versionDetails = await getPricingDealVersions(
        Number(dealDetails['dealId'])
      );
      if (versionDetails.error_status === 401) {
        setTokenExpired(true);
      } else {
        const transformedVersionDetails = versionDetails.map(
          (version: any) => ({
            key: version.version_id,
            "Deal Id": dealDetails.dealId,
            "Version Name": version.version_name,
            "Total Deal Price": formatCurrency(version.total_project_price, version.currency),
            "Deal Discount Amount": formatCurrency(
              version.project_discount_amount, version.currency
            ),
            "Net Deal Price": formatCurrency(version.net_project_price, version.currency ),
            "Total Deal Labor Cost": formatCurrency(
              version.total_project_labor_cost, version.currency
            ),
            "Projected Client Margin %": formatPercentage(version.project_client_margin ?? 0),
            "Target Client Margin %": formatPercentage(version.target_client_margin ?? 0),
            "Variance to Target Margin": formatPercentage(version.variance_to_target_margin ?? 0),
            "Updated By": version.updated_by,
            "Updated On": dayjs(version.updated_on).tz('America/New_York').format(
              "MMM D, YYYY, h:mm:ss A z"
            ),
            "ODE Status": version.ode_status ?? false,
            "Resource Request Status": version.resource_request_status ?? "",
            "HubSpot Update Status": version.hs_line_items_updated_status ?? "",
            "Currency": currencySymbolsMap[version.currency] || '$'
          })
        );
        setDealVersionSource(transformedVersionDetails);
      }
    };
    fetchVersionDetails().then(() => {
      setLoading(false)
    })
  }, [expanded, cloning, openUnlockODEModal]);

  const handleNavigate = (dealId: number, versionName: string, action: string) => {
    if(action === 'edit') {
      navigate(`/edit-pricing-details?dealId=${dealId}&versionName=${encodeURIComponent(versionName)}`);  // Navigate to /edit-pricing page
    } else if(action === 'view') {
      navigate(`/view-pricing-details?dealId=${dealId}&versionName=${encodeURIComponent(versionName)}`);
    }
  };

  const onModalOpen = () => {
    if (versionNameInputRef.current) {
      versionNameInputRef.current.select();
    }
  };
  const isAnyODEStatusTrue = dealVersionSource.some((record: any) => record['ODE Status']);
  const lockedDealVersionInfo = dealVersionSource.filter((record: any) => record['ODE Status']);

  const isEditAndCopyDisabled = (record: any) => {
    return isAnyODEStatusTrue && !record['ODE Status'];
  };

  const isAdmin = JSON.parse(localStorage.getItem('user_details') as string).features.includes('FE_ADMIN') ? true : false

  
  const handleODEUnlock = async () => {
    if(isAdmin  && modalFormInput == '' && (dealDetails['Stage'] as string).includes("Closed")) {
      message.error("Please enter reason to unlock")
      setModalFormError(true)
      return
    }
    setLoading(true);
    
    let lockFn = UnlockPricingDeal(parseInt(dealDetails["dealId"]), modalFormInput, additionalComments)
    await lockFn
      .then((resp) => {
        setLoading(false);
        if(resp.error ?? '') {
          message.error(resp.error)
          return
        }
        message.success("Success");
        setOpenUnlockODEModal(false)
      })
      .catch((error) => {
        setLoading(false);
        message.error("Error Unlocking the Deal");
      });
  };

  const columns = [
    {
      dataIndex: 'action', key: 'action',
      render: (_: any, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={'Duplicate Version'}>
            <Button style={{width: '6px', marginRight: '-4px'}} type="text" 
              disabled={!dealDetails.canEditPricing || isEditAndCopyDisabled(record)}
              onClick={() => {
                setOpenDuplicateVersionModal(true)
                setDuplicateDeal({ "id": record['Deal Id'], "name": record['Version Name'] })
                setNewDuplicateVersionName(`Copy of ${record['Version Name']}`)
              }} 
            >
              <CopyOutlined key="duplicate" style={{ color: `${!dealDetails.canEditPricing || isEditAndCopyDisabled(record) ? 'gray' :'#4096ff'}` }} />
            </Button>
          </Tooltip>
          <Tooltip title={'Edit Version'}>
            <Link to={`/edit-pricing-details?dealId=${record['Deal Id']}&versionName=${encodeURIComponent(record['Version Name'])}`} style={{ textDecoration: 'none' }}>
              <Button style={{width: '6px', marginLeft: '-4px'}} type="text"
                disabled={!dealDetails.canEditPricing || isEditAndCopyDisabled(record)}
              >
                <EditOutlined key="edit" style={{ color: `${!dealDetails.canEditPricing || isEditAndCopyDisabled(record) ? 'gray' :'#4096ff'}` }}  />
              </Button>
            </Link>
          </Tooltip>
          <Tooltip title={'View Version'}>
            <Link to={`/view-pricing-details?dealId=${record['Deal Id']}&versionName=${encodeURIComponent(record['Version Name'])}`} style={{ textDecoration: 'none' }}>
              <Button style={{width: '6px', marginLeft: '-8px'}} type="text">
                <EyeTwoTone />
              </Button>
            </Link>
          </Tooltip>
          {(isAdmin && record['ODE Status']) && <Tooltip title={'Unlock Version'}>
            <Button style={{width: '6px', marginRight: '-4px'}} type="text" 
              onClick={() => {
                setOpenUnlockODEModal(true)
              }} 
            >
              <LockTwoTone key="unlock-ode" style={{ color: `${!dealDetails.canEditPricing || isEditAndCopyDisabled(record) ? 'gray' :'#4096ff'}` }} />
            </Button></Tooltip>}
        </div>
      ),
    },
    {
      title: 'Version Name', dataIndex: 'Version Name', key: 'Version Name',
      render: (text: string, record: any) => (
        <div>
          {text} {record['ODE Status'] && <Tag color="#cd201f">ODE</Tag>}
        </div>
      )
    },
    // {
    //   title: 'Total Deal Amount', dataIndex: 'Total Deal Price', key: 'Total Deal Price', render: (text: string) => (
    //     <div style={{ position: 'relative', width: '100%', textAlign: 'center' }}>
    //       {text}
    //     </div>
    //   ),
    // },
    // {
    //   title: 'Discount Amount', dataIndex: 'Deal Discount Amount', key: 'Deal Discount Amount', render: (text: string) => (
    //     <div style={{ position: 'relative', width: '100%', textAlign: 'center' }}>
    //       {text}
    //     </div>
    //   ),
    // },
    {
      title: 'Net Deal Amount', dataIndex: 'Net Deal Price', key: 'Net Deal Price', render: (_: any, record: any) => (
        <>
        {currencySymbolsMap[dealDetails['currency']] !== record['Currency'] ? (
          <Tooltip title={`Version currency does not match deal currency`}>
          <div style={{ position: 'relative', width: '100%', textAlign: 'center',  fontWeight: 'bold', color: '#ff4040ff' }}>
            {record['Net Deal Price']}
          </div> </Tooltip>
        ) : (
          <div style={{ position: 'relative', width: '100%', textAlign: 'center', fontWeight: 'bold' }}>
            {record['Net Deal Price']}
          </div>
        )}</>
      )
    },
    {
      title: 'Projected Client Margin %', dataIndex: 'Projected Client Margin %', textAlign: 'center', key: 'Projected Client Margin %', render: (text: string) => (
        <div style={{ position: 'relative', width: '100%', textAlign: 'center' }}>
          {text}
        </div>
      )
    },
    {
      title: 'Variance to Target Margin', dataIndex: 'Variance to Target Margin', key: 'Variance to Target Margin', render: (text: string) => (
        <div style={{ position: 'relative', width: '100%', textAlign: 'center', fontWeight: 'bold', color: parseFloat(text) < 0 ? '#F3797E' : text == '-0.7%' ? 'orange' : '#6FBE73' }}>
          {text}
        </div>
      )
    },
    { title: 'HubSpot Update Status', dataIndex: 'HubSpot Update Status', key: 'HubSpot Update Status' },
    { title: 'Resource Request Status', dataIndex: 'Resource Request Status', key: 'Resource Request Status' },
    { title: 'Updated By', dataIndex: 'Updated By', key: 'Updated By' },
    { title: 'Updated On', dataIndex: 'Updated On', key: 'Updated On' },
  ];
  // const dataSource = dealVersionSource.map((item: any, index) => ({
  //   ...item,
  //   action: <EditOutlined key="edit" style={{ color: "#4096ff" }} />
  // }));

  const cloneDealPricingVersion = async () => {
    if (newDuplicateVersionName.trim().length < 4) {
      message.error('Name should be greater than 3 letters');
      return;
    }
    setCloning(true)
    const cloneVersion = await cloneDealVersion(duplicateDeal['id'], duplicateDeal['name'], newDuplicateVersionName);
    if (cloneVersion.error_status === 401) {
      setTokenExpired(true);
    } else {
      if (cloneVersion.error) {
        console.error('Error Response Data:', cloneVersion.error);
        console.error('Error Response Status:', cloneVersion.error_status);
        message.error(cloneVersion.error);
      } else {
        message.success("Cloned Successfully")
        setOpenDuplicateVersionModal(false)
      }
    }
    setCloning(false)
  }



  const createDealPricingVersion = async () => {
    if (newCreateVersionName.trim().length < 4) {
      message.error('Name should be greater than 3 letters');
      return;
    }
    setCloning(true)
    const createVersion = await createNewPricingDealVersion(dealDetails.dealId, newCreateVersionName);
    if (createVersion.error_status === 401) {
      setTokenExpired(true);
    } else {
      if (createVersion.error) {
        console.error('Error Response Data:', createVersion.error);
        console.error('Error Response Status:', createVersion.error_status);
        message.error(createVersion.error);
      } else {
        message.success("Created Successfully")
        navigate(`/edit-pricing-details?dealId=${dealDetails.dealId}&versionName=${encodeURIComponent(newCreateVersionName)}`);  // Navigate to /edit-pricing page
        setOpenCreateVersionModal(false)
      }
    }
    setCloning(false)
  }



  return tokenExpired ? (
    <LogoutPopUp openPopup={tokenExpired} />
  ) : (
    <Card title="" style={{ marginBottom: "16px" }}>
      <Table
        dataSource={dealVersionSource}
        columns={columns}
        pagination={false}
        size="small"
        rowClassName={(record) => `${record['ODE Status'] ? 'light-red-row' : ''} small-table-row`}
        style={{ color: "#888888" }}
        loading={loading}
      />

      <Modal
        open={openCreateVersionModal}
        onOk={() => {
          createDealPricingVersion()
        }}
        onCancel={() => {
          setOpenCreateVersionModal(false)
        }}
        afterOpenChange={onModalOpen}
        closable={false}
        confirmLoading={cloning}
      >
        <>
          <label htmlFor="versionName" style={{ paddingBottom: '8px' }}>Enter New Version Name:</label>
          <Input ref={versionNameInputRef} id="versionName" onChange={(e: any) => setNewCreateVersionName(e.target.value)} value={newCreateVersionName} />
        </>
      </Modal>

      <Modal
        open={openDuplicateVersionModal}
        onOk={() => {
          cloneDealPricingVersion()
        }}
        onCancel={() => {
          setOpenDuplicateVersionModal(false)
        }}
        afterOpenChange={onModalOpen}
        closable={false}
        confirmLoading={cloning}
      >
        <>
          <label htmlFor="versionName" style={{ paddingBottom: '8px' }}>Enter New Version Name:</label>
          <Input ref={versionNameInputRef} id="versionName" onChange={(e: any) => setNewDuplicateVersionName(e.target.value)} value={newDuplicateVersionName} />
        </>
      </Modal>
      {lockedDealVersionInfo.length > 0 && <Modal
          title={"Unlock the Deal?"}
          open={openUnlockODEModal}
          onCancel={() => setOpenUnlockODEModal(false)}
          onOk={handleODEUnlock}
          okText={"Confirm"}
          okButtonProps={{ loading: loading }}
          width={'70%'}
        > 
        <>
          <p>
            <span style={{ fontWeight: "bold" }}>Deal : </span>
            {dealDetails['dealName']}
          </p>
          <p>
            <span style={{ fontWeight: "bold" }}>Version : </span>
            {lockedDealVersionInfo[0]['Version Name']}
          </p>
          {(isAdmin && ((dealDetails['Stage'] as string).includes("Closed") || dealDetails["workAhead"] === "Yes")) &&
            <>
              <p>
                <span style={{ fontWeight: "bold" }}>Stage : </span>
                {dealDetails['Stage']}
              </p>
              <p>
                <span style={{ fontWeight: "bold" }}>Reason to Unlock* <Tooltip title={`Please add reason to unlock this deal`}><InfoCircleOutlined /></Tooltip> </span>
                <TextArea value={modalFormInput} onChange={(e)=>setModalFormInput(e.target.value)} status={modalFormError ? 'error': ''}/>
              </p>
              <p>
                <span style={{ fontWeight: "bold" }}>Additional Comments </span>
                <TextArea value={additionalComments} onChange={(e)=>setAdditionalComments(e.target.value)}/>
              </p>
            </>
          
          }
          </>
        </Modal>}
        <Button
          type="default"
          onClick={() => {
            setOpenCreateVersionModal(true);
            setNewCreateVersionName(`Version - ${dayjs().format('MMMM DD, YYYY hh:mm A')}`);
          }} 
          disabled={!dealDetails.canEditPricing || dealDetails.odeStatus === "Submitted"}
          style={{ marginTop: '16px', width: '100%' }}>
          Create New Version</Button>
    </Card>
  );
};

export default DealEconomicsSummariesTable;
